import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ButtonV2 } from '@medeloop/medeloop-components-library';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'motion/react';
import { selectAuthToken } from 'src/store/selectors';
import { useGetDataSourceById } from 'src/api/data-sources-queries';
import { getAuditActivity, type AuditActivityItem } from 'src/api/audit';

const ESTIMATED_DURATION_MS = 3 * 60 * 1000;

const STATUS_MESSAGES = [
  'Fetching audit records...',
  'Processing analyst queries...',
  'Scanning notebook activity...',
  'Gathering shared links...',
  'Compiling results...',
];

function getStatusMessage(progress: number): string {
  const index = Math.min(
    Math.floor((progress / 100) * STATUS_MESSAGES.length),
    STATUS_MESSAGES.length - 1,
  );
  return STATUS_MESSAGES[index];
}

function formatTimestamp(ts: string): string {
  return new Date(ts)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, ' UTC');
}

function formatAction(action: string): string {
  if (action === 'analytics_query') return 'Analyst Query';
  if (action === 'notebook_cell_execution') return 'Notebook Cell Execution';
  if (action === 'shared_analysis_link') return 'Shared Analysis Link';
  if (action === 'shared_notebook_link') return 'Shared Notebook Link';
  return action;
}

export function AuditLog() {
  const { datasetId } = useParams();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportError, setReportError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const token = useSelector(selectAuthToken);
  const { data: dataSource } = useGetDataSourceById(datasetId);

  const datasetName = dataSource?.displayName ?? 'Dataset';

  useEffect(() => {
    if (!isDownloading) {
      setProgress(0);
      return;
    }

    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min((elapsed / ESTIMATED_DURATION_MS) * 100, 99));
    }, 200);

    return () => clearInterval(interval);
  }, [isDownloading]);

  const downloadCsv = async () => {
    if (!startDate || !endDate || !datasetId || !token) return;
    setIsDownloading(true);
    setReportError(null);
    try {
      const res = await getAuditActivity({
        token,
        datasourceId: datasetId,
        startDate,
        endDate,
      });
      const header = 'Event ID,Timestamp,User ID,Email,Action';
      const rows = res.items.map(
        (item: AuditActivityItem) =>
          `"${item.eventId}","${formatTimestamp(item.timestamp)}","${item.userId}","${item.email}","${formatAction(item.action)}"`,
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${datasetName}-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Failed to download');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <h2 className="text-2xl font-bold text-white mb-3">{datasetName} Audit Logs</h2>
      <p className="text-content-on-dark-subdued mb-10">
        Official audit record of dataset access events for the {datasetName} dataset. Download a CSV
        filtered by date range. All timestamps are in UTC.
      </p>

      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-white">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-transparent border border-stroke-on-dark-subdued rounded-lg px-4 py-3 text-sm text-content-on-dark-subdued [color-scheme:dark]"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-white">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-transparent border border-stroke-on-dark-subdued rounded-lg px-4 py-3 text-sm text-content-on-dark-subdued [color-scheme:dark]"
          />
        </div>
      </div>

      {reportError && (
        <p className="text-sm text-red-400 mb-4" role="alert">
          {reportError}
        </p>
      )}

      <AnimatePresence mode="wait">
        {isDownloading ? (
          <motion.div
            key="progress"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="w-full rounded-lg border border-stroke-on-dark-subdued bg-gray-900 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <motion.span
                key={getStatusMessage(progress)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="text-sm text-content-on-dark-subdued"
              >
                {getStatusMessage(progress)}
              </motion.span>
              <span className="text-sm font-semibold text-white tabular-nums">
                {Math.round(progress)}%
              </span>
            </div>

            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'linear-gradient(to right, #21BDB9, #22D3EE)' }}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>

            <p className="text-xs text-center text-content-on-dark-subdued">
              Preparing your audit log — this may take a few minutes for large datasets
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="button"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
          >
            <ButtonV2
              variant="secondary"
              size="lg"
              onClick={downloadCsv}
              disabled={!startDate || !endDate}
              className="w-full disabled:!bg-gray-700 disabled:!text-gray-400 disabled:!opacity-100"
            >
              Download CSV
            </ButtonV2>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-sm text-content-on-dark-subdued mt-4">
        Includes access events for Medeloop AI Analyst, Notebooks, and Shared Links for the{' '}
        {datasetName} dataset.
      </p>
    </div>
  );
}
